/**
 * Work Front Service
 * API das frentes de obra (#365): cadastro, etapas, riscos marcados e trabalhadores.
 */
import apiClient from './apiClient';
import type {
  CreateWorkFrontInput,
  CreateWorkFrontWorkerInput,
  ReplaceWorkFrontRisksInput,
  UpdateWorkFrontInput,
  UpdateWorkFrontStageInput,
  UpdateWorkFrontWorkerInput,
  WorkFrontDependencies,
  WorkFrontDetail,
  WorkFrontEligibleEmployee,
  WorkFrontListItem,
  WorkFrontObra,
  WorkFrontRisksResponse,
  WorkFrontStageInput,
  WorkFrontStageItem,
  WorkFrontStatus,
  WorkFrontSummary,
  WorkFrontWorkerItem,
} from '../types/workFront';

export * from '../types/workFront';

type Id = string | number;

export interface WorkFrontFilters {
  unitId?: Id;
  companyId?: Id;
  status?: WorkFrontStatus;
  search?: string;
}

const FILTROS_DA_QUERY = ['unitId', 'companyId', 'status', 'search'] as const;

function montarParams(filtros: WorkFrontFilters): Record<string, string> {
  const params: Record<string, string> = {};
  FILTROS_DA_QUERY.forEach((nome) => {
    const valor = filtros[nome];
    if (valor !== undefined && valor !== '') params[nome] = String(valor);
  });
  return params;
}

class WorkFrontService {
  private baseUrl = '/work-fronts';

  async list(filtros: WorkFrontFilters = {}): Promise<WorkFrontListItem[]> {
    const response = await apiClient.get(this.baseUrl, { params: montarParams(filtros) });
    return response.data.data || response.data;
  }

  async summary(filtros: WorkFrontFilters = {}): Promise<WorkFrontSummary> {
    const response = await apiClient.get(`${this.baseUrl}/summary`, { params: montarParams(filtros) });
    return response.data.data || response.data;
  }

  async obras(companyId?: Id): Promise<WorkFrontObra[]> {
    const response = await apiClient.get(`${this.baseUrl}/obras`, {
      params: companyId ? { companyId } : undefined,
    });
    return response.data.data || response.data;
  }

  async getById(id: Id): Promise<WorkFrontDetail> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data.data || response.data;
  }

  async create(data: CreateWorkFrontInput): Promise<WorkFrontListItem> {
    const response = await apiClient.post(this.baseUrl, data);
    return response.data.data || response.data;
  }

  async update(id: Id, data: UpdateWorkFrontInput): Promise<WorkFrontListItem> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    return response.data.data || response.data;
  }

  async close(id: Id): Promise<WorkFrontListItem> {
    const response = await apiClient.post(`${this.baseUrl}/${id}/close`);
    return response.data.data || response.data;
  }

  async reopen(id: Id): Promise<WorkFrontListItem> {
    const response = await apiClient.post(`${this.baseUrl}/${id}/reopen`);
    return response.data.data || response.data;
  }

  async remove(id: Id): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  async dependencies(id: Id): Promise<WorkFrontDependencies> {
    const response = await apiClient.get(`${this.baseUrl}/${id}/dependencies`);
    return response.data.data || response.data;
  }

  async createStage(frontId: Id, data: WorkFrontStageInput): Promise<WorkFrontStageItem> {
    const response = await apiClient.post(`${this.baseUrl}/${frontId}/stages`, data);
    return response.data.data || response.data;
  }

  async updateStage(frontId: Id, stageId: Id, data: UpdateWorkFrontStageInput): Promise<WorkFrontStageItem> {
    const response = await apiClient.put(`${this.baseUrl}/${frontId}/stages/${stageId}`, data);
    return response.data.data || response.data;
  }

  async deleteStage(frontId: Id, stageId: Id): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${frontId}/stages/${stageId}`);
  }

  async getRisks(frontId: Id): Promise<WorkFrontRisksResponse> {
    const response = await apiClient.get(`${this.baseUrl}/${frontId}/risks`);
    return response.data.data || response.data;
  }

  async replaceRisks(frontId: Id, data: ReplaceWorkFrontRisksInput): Promise<WorkFrontRisksResponse> {
    const response = await apiClient.put(`${this.baseUrl}/${frontId}/risks`, data);
    return response.data.data || response.data;
  }

  async listWorkers(frontId: Id): Promise<WorkFrontWorkerItem[]> {
    const response = await apiClient.get(`${this.baseUrl}/${frontId}/workers`);
    return response.data.data || response.data;
  }

  async addWorker(frontId: Id, data: CreateWorkFrontWorkerInput): Promise<WorkFrontWorkerItem> {
    const response = await apiClient.post(`${this.baseUrl}/${frontId}/workers`, data);
    return response.data.data || response.data;
  }

  async updateWorker(frontId: Id, linkId: Id, data: UpdateWorkFrontWorkerInput): Promise<WorkFrontWorkerItem> {
    const response = await apiClient.put(`${this.baseUrl}/${frontId}/workers/${linkId}`, data);
    return response.data.data || response.data;
  }

  async removeWorker(frontId: Id, linkId: Id): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${frontId}/workers/${linkId}`);
  }

  async eligibleEmployees(unitId: Id): Promise<WorkFrontEligibleEmployee[]> {
    const response = await apiClient.get(`${this.baseUrl}/eligible-employees`, { params: { unitId } });
    return response.data.data || response.data;
  }
}

export const workFrontService = new WorkFrontService();
export default workFrontService;
